import { useCallback, useEffect, useState } from "react";
import { Copy, Eye, EyeOff, RefreshCw, Plug } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useApi, errMsg } from "@/lib/api";

const Secret = ({ label, value, testid }) => {
  const [show, setShow] = useState(false);
  const copy = () => { navigator.clipboard.writeText(value); toast.success("Copiado"); };
  return (
    <div>
      <p className="text-xs font-semibold text-slate-600">{label}</p>
      <div className="flex gap-2 mt-1">
        <code className="field flex items-center font-mono text-xs truncate" data-testid={testid}>{value ? (show ? value : "•".repeat(28)) : "…"}</code>
        <button className="icon-btn" onClick={() => setShow(!show)} data-testid={`${testid}-toggle`}>{show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
        <button className="icon-btn" onClick={copy} data-testid={`${testid}-copy`}><Copy className="w-4 h-4" /></button>
      </div>
    </div>
  );
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("es-ES") : "Nunca");

export const SyncTab = () => {
  const call = useApi();
  const [info, setInfo] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const load = useCallback(() => call("get", "/admin/woo-info").then((r) => setInfo(r.data)).catch((e) => toast.error(errMsg(e))), [call]);
  useEffect(() => { load(); }, [load]);

  const regenerate = async () => {
    setConfirm(false);
    try { await call("post", "/admin/woo-keys"); toast.success("Claves nuevas generadas. Las anteriores ya no funcionan."); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copiado"); };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="admin-sync-tab">
      <div className="bg-white border p-6 space-y-5">
        <p className="filter-title flex items-center gap-2"><Plug className="w-4 h-4" /> Conector ClassicGes (modo WooCommerce)</p>
        <p className="text-sm text-slate-600">La tienda se comporta como una tienda WooCommerce. Entrega estos tres datos a SOLINFOCOMERCIO y configuran su conector igual que con cualquier WooCommerce. ClassicGes manda en <b>referencia, precio normal y rebajado, tallas, colores y stock</b>; las fotos, el nombre web y las descripciones se gestionan aquí y nunca se sobrescriben. Los artículos nuevos llegan <b>ocultos</b> y se ponen a la venta solos al tener foto y stock. Los pedidos (también los pendientes de pago) los recoge ClassicGes desde aquí. El outlet sigue con la hoja de Google.</p>
        <div>
          <p className="text-xs font-semibold text-slate-600">URL de la tienda (en el conector)</p>
          <div className="flex gap-2 mt-1"><code className="field flex items-center font-mono text-xs truncate" data-testid="woo-store-url">{info?.store_url || "…"}</code><button className="icon-btn" onClick={() => info && copy(info.store_url)} data-testid="woo-store-url-copy"><Copy className="w-4 h-4" /></button></div>
        </div>
        <Secret label="Clave de cliente (consumer key)" value={info?.consumer_key} testid="woo-ck" />
        <Secret label="Clave secreta (consumer secret)" value={info?.consumer_secret} testid="woo-cs" />
        <button className="btn-outline !h-10" onClick={() => setConfirm(true)} data-testid="woo-regenerate-button"><RefreshCw className="w-4 h-4 mr-2" /> Generar claves nuevas</button>
      </div>
      <div className="bg-white border p-6 space-y-4">
        <p className="filter-title">Estado de la conexión</p>
        <div className="grid sm:grid-cols-3 gap-3 text-sm">
          <div className="bg-slate-50 p-3"><p className="text-xs text-slate-500">Última conexión</p><p className="font-bold" data-testid="woo-last-connection">{info ? fmt(info.last_connection) : "…"}</p></div>
          <div className="bg-slate-50 p-3"><p className="text-xs text-slate-500">Peticiones 24 h</p><p className="font-bold text-lg" data-testid="woo-requests">{info?.requests_24h ?? "…"}</p></div>
          <div className="bg-slate-50 p-3"><p className="text-xs text-slate-500">Artículos desde ClassicGes</p><p className="font-bold text-lg" data-testid="woo-synced">{info?.synced_products ?? "…"}</p></div>
        </div>
        <p className="text-xs font-semibold text-slate-600">Últimas peticiones del conector</p>
        {info && info.log.length === 0 && <p className="text-sm text-slate-500" data-testid="woo-log-empty">Todavía no se ha conectado nadie.</p>}
        <ul className="divide-y text-xs font-mono" data-testid="woo-log">
          {info?.log.map((l, i) => (
            <li key={i} className="py-1.5 flex gap-3"><span className="text-slate-400 w-32 shrink-0">{new Date(l.at).toLocaleString("es-ES")}</span><span className={`w-14 shrink-0 font-bold ${l.ok ? "text-verde" : "text-rose-600"}`}>{l.method}</span><span className="truncate">{l.path}{l.query ? `?${l.query}` : ""}</span>{!l.ok && <span className="text-rose-600 shrink-0">clave incorrecta</span>}</li>
          ))}
        </ul>
      </div>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>¿Generar claves nuevas?</AlertDialogTitle><AlertDialogDescription>Las claves actuales dejarán de funcionar y habrá que volver a configurar el conector de ClassicGes con las nuevas.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="woo-regenerate-cancel">Cancelar</AlertDialogCancel><AlertDialogAction onClick={regenerate} data-testid="woo-regenerate-confirm">Generar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
